import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import TextField from '@mui/material/TextField';
import StandardButton from '../StandardButton';
import { AddressFormValues, checkForError, handleSubmit } from './utils';

interface AddressInputFormProps {
  initialValues?: AddressFormValues;
  onSave?: (values: AddressFormValues) => void;
  /** Called whenever any form field changes. Allows parent to track real-time form state. */
  onChange?: (values: AddressFormValues) => void;
}

const defaultInitialValues: AddressFormValues = {
  streetAddress: '',
  unit: '',
  city: '',
  country: '',
  postCode: '',
  formattedAddress: '',
};

const AddressInputForm: React.FC<AddressInputFormProps> = ({ initialValues, onSave, onChange }) => {
  const { t } = useTranslation();
  const [formValues, setFormValues] = useState<AddressFormValues>(
    initialValues || defaultInitialValues,
  );

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const updatedValues = { ...formValues, [name]: value };
    setFormValues(updatedValues);
    // Notify parent component of state changes in real-time
    onChange?.(updatedValues);
  };

  const getError = (fieldName: keyof AddressFormValues) => {
    return checkForError(fieldName, formValues);
  };

  const streetAddressHelperText = getError('streetAddress') ? t('addressForm.required') : '';
  const cityHelperText = getError('city') ? t('addressForm.required') : '';
  const postCodeHelperText = getError('postCode') ? t('addressForm.required') : '';

  const isValid = !getError('streetAddress') && !getError('city') && !getError('postCode');

  return (
    <>
      <div style={styles.formContainer}>
        <TextField
          name="streetAddress"
          label={t('addressForm.streetAddress')}
          placeholder={t('addressForm.streetAddress')}
          onChange={handleChange}
          value={formValues.streetAddress}
          error={getError('streetAddress')}
          helperText={streetAddressHelperText}
          size="medium"
          variant="outlined"
        />
        <TextField
          name="unit"
          label={t('addressForm.unit')}
          placeholder={t('addressForm.unitPlaceholder')}
          slotProps={{ inputLabel: { shrink: true } }}
          onChange={handleChange}
          value={formValues.unit}
          size="medium"
          variant="outlined"
        />
        <TextField
          name="city"
          label={t('addressForm.city')}
          placeholder={t('addressForm.city')}
          onChange={handleChange}
          value={formValues.city}
          error={getError('city')}
          helperText={cityHelperText}
          size="medium"
          variant="outlined"
        />
        <TextField
          name="country"
          disabled
          label={t('addressForm.country')}
          placeholder={t('addressForm.country')}
          onChange={handleChange}
          value={formValues.country}
          size="medium"
          variant="outlined"
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: '#F5F5F5',
            },
            '& .MuiInputBase-input.Mui-disabled': {
              WebkitTextFillColor: '#000000',
            },
          }}
        />
        <TextField
          name="postCode"
          label={t('addressForm.postCode')}
          placeholder={t('addressForm.postCodePlaceholder')}
          onChange={handleChange}
          value={formValues.postCode}
          size="medium"
          error={getError('postCode')}
          helperText={postCodeHelperText}
          variant="outlined"
          slotProps={{ inputLabel: { shrink: true } }}
        />
      </div>

      <div style={styles.buttonContainer}>
        <StandardButton
          label={t('addressForm.save')}
          onClick={() => {
            if (!isValid) return;
            handleSubmit(formValues, onSave);
          }}
          themeKey="primaryButton"
          containerStyle={styles.saveButton}
        />
      </div>
    </>
  );
};

const styles: Styles = {
  formContainer: {
    padding: '20px 24px',
    display: 'flex',
    flexDirection: 'column',
    gap: '30px',
  },
  buttonContainer: {
    display: 'flex',
    padding: '16px 24px',
    backgroundColor: '#ffffff',
    justifyContent: 'center',
  },
  saveButton: {
    width: '100%',
  },
};

export default AddressInputForm;
